<script lang="ts" setup>
import { useRequestURL } from 'nuxt/app';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRoute } from 'vue-router';
import feedbackSuccessIllustration from '~/assets/images/support/feedback-success.svg';
import { PageHero } from '~/components';
import {
  AppBreadcrumbs,
  AppButton,
  AppTextarea,
  AppTextInput,
  AppTypography,
} from '~/components/base';
import {
  MAX_ATTACHMENTS,
  useReportProblem,
} from '~/composables/useReportProblem';
import { REPORT_ENTITY_SECTIONS } from '~/utils/enums/navigation';

definePageMeta({ layout: 'default' });

useHead({ title: 'Help Us Improve' });
useSeoMeta({
  description:
    'Spotted something wrong or inaccurate? Tell us so we can fix it.',
  ogTitle: 'Help Us Improve | VoteWise',
  ogDescription:
    'Spotted something wrong or inaccurate? Tell us so we can fix it.',
});

const DESCRIPTION_LIMIT = 280;

const route = useRoute();

const reportType = computed(() => {
  const value = route.query.report_type;
  return typeof value === 'string' ? value : null;
});
const reportId = computed(() => {
  const value = route.query.report_id;
  return typeof value === 'string' ? value : null;
});

// Public page each report_type's slug resolves to — shown so the reporter
// can confirm which page they're flagging, and used to link back to it.
const entity = computed(() => {
  if (!reportType.value || !reportId.value) return null;
  const section = REPORT_ENTITY_SECTIONS[reportType.value];
  if (!section) return null;
  return {
    reportType: reportType.value,
    reportId: reportId.value,
    url: `${section.to}/${reportId.value}`,
  };
});

// VTW-497: the opener can name the exact URL to return to, for the cases where
// the entity's own Details page is not what the reporter was looking at —
// desktop renders a campaign as a popup over /campaigns, so Back has to reopen
// that popup rather than navigate to the standalone page.
//
// This value reaches a router navigation and is rendered as an anchor href, so
// it is restricted to internal paths: a single leading slash, with the second
// character neither `/` nor `\`. Both variants escape the site — for HTTP(S)
// the URL parser treats a backslash in the authority position as a slash, so
// `/\evil.example` normalises to `//evil.example` and resolves to
// `https://evil.example/`. Checking only for `//` would miss that. Anything
// rejected is dropped rather than sanitised, and the entity fallback applies.
const returnTo = computed(() => {
  const value = route.query.return_to;
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/')) return null;
  if (value[1] === '/' || value[1] === '\\') return null;
  return value;
});

// Arrived via an entity's "Help us improve this information" link: the crumb
// (and its back button) should return to that specific Candidate/Party/
// Campaign details page, not Support and not just the section's list page —
// the reporter never came from the Help Centre in that flow, and landing on
// the list page still loses the record they were reporting on.
const backTarget = computed(() => returnTo.value ?? entity.value?.url ?? null);

const breadcrumbs = computed(() => {
  const section = reportType.value
    ? REPORT_ENTITY_SECTIONS[reportType.value]
    : null;
  const first = section
    ? { label: section.label, to: backTarget.value ?? section.to }
    : { label: 'Support', to: '/support' };
  return [first, { label: 'Help Us Improve' }];
});

const requestUrl = useRequestURL();
const entityPublicUrl = computed(() =>
  entity.value ? `${requestUrl.origin}${entity.value.url}` : '',
);

const heading = computed(() =>
  entity.value ? 'Review Information' : 'Help Us Improve',
);
// The entity-report flow (arrived via report_type/report_id) uses "review"
// framing on the success screen since the reporter is confirming their
// details rather than filing free-form feedback; the generic Help Us Improve
// flow from the Support page keeps the original copy.
const successTitle = computed(() =>
  entity.value ? 'Thank you for your review!' : 'Thank you for your feedback!',
);
const successSubtitle = computed(() =>
  entity.value
    ? 'Your feedback helps us keep VoteWise accurate and reliable.'
    : 'We appreciate your help in making VoteWise better.',
);
// Both flows submit in one step — the Review Information frame labels its
// button "Submit" too, so the entity-report variant no longer says "Continue".
const submitLabel = 'Submit';

const {
  subject,
  description,
  attachments,
  pending,
  isSuccess,
  isError,
  errorMessage,
  submit,
} = useReportProblem(
  entity.value
    ? { reportType: entity.value.reportType, reportId: entity.value.reportId }
    : null,
);

// One preview per selected file, index-aligned with `attachments`.
const previews = ref<{ url: string; name: string }[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);

const canAddAttachment = computed(
  () => attachments.value.length < MAX_ATTACHMENTS,
);

const canSubmit = computed(
  () => subject.value.trim().length > 0 && description.value.trim().length > 0,
);

function openFilePicker() {
  fileInput.value?.click();
}

function onFileSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  const selected = Array.from(target.files ?? []);

  // The picker is `multiple`, so a single interaction can exceed the cap;
  // take only what still fits rather than rejecting the whole selection.
  const remaining = MAX_ATTACHMENTS - attachments.value.length;
  selected.slice(0, Math.max(0, remaining)).forEach((file) => {
    attachments.value.push(file);
    previews.value.push({ url: URL.createObjectURL(file), name: file.name });
  });

  // Reset the input so re-picking the same file still fires `change`.
  if (fileInput.value) fileInput.value.value = '';
}

// The blob URLs must also be released when the user navigates away (or
// after submit) — revoking only on remove leaks them otherwise.
onBeforeUnmount(() => {
  previews.value.forEach((preview) => URL.revokeObjectURL(preview.url));
});

function removeAttachment(index: number) {
  const [preview] = previews.value.splice(index, 1);
  if (preview) URL.revokeObjectURL(preview.url);
  attachments.value.splice(index, 1);
}

async function submitFeedback() {
  if (!canSubmit.value || pending.value) return;
  await submit();
}
</script>

<template>
  <div class="report-a-problem-page">
    <PageHero constrained max-width="max-w-3xl">
      <div data-testid="report-a-problem-page">
        <AppBreadcrumbs
          :custom-breadcrumbs="breadcrumbs"
          :back-fallback="backTarget ?? undefined"
          class="hidden px-0 lg:flex"
        />

        <div class="report-a-problem-page__card">
          <AppTypography
            type="title-5"
            element="h1"
            class="text-color-primary mb-6 text-left lg:block lg:text-center"
          >
            {{ heading }}
          </AppTypography>

          <template v-if="!isSuccess">
            <form
              class="report-a-problem-page__form"
              @submit.prevent="submitFeedback"
            >
              <AppTextInput
                v-if="entity"
                :model-value="entityPublicUrl"
                :floating-label="false"
                disabled
                data-testid="report-entity-url"
              />

              <AppTextInput
                v-model="subject"
                label="Subject"
                data-testid="report-subject"
              />

              <div class="report-a-problem-page__textarea">
                <AppTextarea
                  v-model="description"
                  label="Description"
                  :rows="6"
                  :maxlength="DESCRIPTION_LIMIT"
                  data-testid="report-description"
                />
                <AppTypography
                  type="caption"
                  class="report-a-problem-page__counter text-color-tertiary"
                >
                  {{ description.length }} / {{ DESCRIPTION_LIMIT }}
                </AppTypography>
              </div>

              <div class="report-a-problem-page__attachment">
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  multiple
                  class="hidden"
                  @change="onFileSelected"
                />

                <button
                  v-if="canAddAttachment"
                  type="button"
                  data-testid="report-attachment-add"
                  class="report-a-problem-page__attachment-add"
                  :aria-label="`Add an attachment (up to ${MAX_ATTACHMENTS})`"
                  @click="openFilePicker"
                >
                  <span aria-hidden="true">+</span>
                </button>

                <div
                  v-for="(preview, index) in previews"
                  :key="preview.url"
                  :data-testid="`report-attachment-preview-${index}`"
                  class="report-a-problem-page__attachment-preview"
                >
                  <img :src="preview.url" :alt="preview.name" />
                  <button
                    type="button"
                    class="report-a-problem-page__attachment-remove"
                    :aria-label="`Remove ${preview.name}`"
                    @click="removeAttachment(index)"
                  >
                    <Icon name="solar:close-circle-bold" size="16" />
                  </button>
                </div>
              </div>

              <AppTypography
                v-if="isError"
                type="footnote"
                class="report-a-problem-page__error text-color-danger"
                data-testid="report-error"
              >
                {{ errorMessage }}
              </AppTypography>

              <AppButton
                type="submit"
                :label="submitLabel"
                data-testid="report-continue"
                class="report-a-problem-page__submit"
                :disabled="!canSubmit || pending"
                :loading="pending"
              />
            </form>
          </template>

          <template v-else>
            <div
              class="report-a-problem-page__success"
              data-testid="report-success"
            >
              <img
                :src="feedbackSuccessIllustration"
                alt=""
                width="241"
                height="180"
                class="report-a-problem-page__success-illustration"
              />
              <AppTypography
                type="title-6"
                element="h2"
                class="text-color-primary"
              >
                {{ successTitle }}
              </AppTypography>
              <AppTypography
                type="body"
                class="report-a-problem-page__success-subtitle"
              >
                {{ successSubtitle }}
              </AppTypography>
              <NuxtLink to="/" class="report-a-problem-page__home-link">
                <AppButton label="Back to Homepage" />
              </NuxtLink>
            </div>
          </template>
        </div>
      </div>
    </PageHero>
  </div>
</template>

<style scoped lang="scss">
.report-a-problem-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__card {
    margin-top: $spacing-md;

    @media (min-width: 1024px) {
      padding: $spacing-2xl;
      border-radius: $radius-2xl;
      background-color: $surface-color-bg;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;

    // Figma: every field on this form is radius/lg (16px). PrimeVue's default
    // is 6px. Scoped to this page rather than changed on `.p-inputtext`
    // globally, which would restyle every form in the app.
    :deep(.p-inputtext),
    :deep(.p-textarea) {
      border-radius: $radius-lg;
    }

    // The disabled "Reporting on" field carries borders/secondary (#827b72) in
    // this frame. The global `.p-inputtext:disabled` rule matches the border to
    // the fill instead, so override it here only. `$border-color-tag` is this
    // repo's name for that value.
    :deep(.p-inputtext:disabled),
    :deep(.p-inputtext.p-disabled) {
      border-color: $border-color-tag;
    }

    // Stretch the floating-label fields to fill the form width (design: the
    // Subject and Description fields span the full 728px form). Without this
    // the PrimeVue inputs shrink to their intrinsic width and look truncated.
    :deep(.app-floating-label-wrapper),
    :deep(.p-floatlabel) {
      display: block;
      width: 100%;
    }

    :deep(.app-input-text),
    :deep(.app-textarea) {
      width: 100%;
    }
  }

  &__textarea {
    position: relative;
  }

  &__counter {
    position: absolute;
    right: $spacing-md;
    bottom: $spacing-md;
    pointer-events: none;
  }

  &__attachment {
    display: flex;
    // Up to 3 previews plus the add tile (4 x 88px) overflows a narrow
    // viewport, so the row wraps instead of shrinking the tiles.
    flex-wrap: wrap;
    align-items: center;
    gap: $spacing-md;
  }

  &__attachment-add {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 88px;
    height: 88px;
    padding: $spacing-2xl;
    border: none;
    border-radius: $radius-md;
    // Figma surfaces/brand—shade-03 (#cfd4d2) and borders/brand—default
    // (#0f2a1d). This app's `$surface-color-brand-shade-3` is a different value
    // (#f2f6f4) despite the shared name, so the design's tone is set literally
    // — same approach as the other Figma-matched surfaces.
    background-color: #cfd4d2;
    color: $brand-color;
    font-size: 24px;
    font-weight: 400;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.15s ease;

    // Plain CSS `border-style: dashed` can't set an exact dash/gap length —
    // the browser picks its own pattern. An inline SVG stroke (rx matched to
    // $radius-md, inset by half the 1px stroke for a crisp line) is the only
    // way to get the Figma-specified 4px dash / 4px gap pixel-exact.
    //
    // KEEP IN SYNC: the `%230f2a1d` stroke is $brand-color, duplicated because a
    // data-URI can't take an SCSS variable (its `#` would read as a fragment).
    // If $brand-color is ever retuned, update this stroke with it — the glyph
    // above picks the new value up on its own and would otherwise mismatch.
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='88' viewBox='0 0 88 88'%3E%3Crect x='0.5' y='0.5' width='87' height='87' rx='11.5' ry='11.5' fill='none' stroke='%230f2a1d' stroke-width='1' stroke-dasharray='4 4'/%3E%3C/svg%3E");
    background-repeat: no-repeat;

    // Figma specifies no hover state for this tile; #c2c9c6 is a manual ~5%
    // darken of the #cfd4d2 base, not a token.
    &:hover {
      background-color: #c2c9c6;
    }
  }

  &__attachment-preview {
    position: relative;
    flex: 0 0 auto;
    width: 88px;
    height: 88px;

    img {
      width: 100%;
      height: 100%;
      border-radius: $radius-lg;
      object-fit: cover;
    }
  }

  &__attachment-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 9999px;
    background-color: $surface-color-bg;
    color: $text-color-primary;
    cursor: pointer;
  }

  &__submit {
    width: 100%;

    :deep(.p-button) {
      width: 100%;
      justify-content: center;
    }
  }

  &__success {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-2xl 0;
    text-align: center;
  }

  // Figma: text/primary (#201e1a) — this app's `.text-color-primary` token is a
  // cooler near-black (#050529), so the warm value is set here as the other
  // Figma-matched cards do. Was `.text-color-tertiary`, which read as muted.
  &__success-subtitle {
    color: #201e1a;
  }

  &__success-illustration {
    width: 241px;
    max-width: 100%;
    height: auto;
    margin-bottom: $spacing-2xl;
  }

  &__home-link {
    margin-top: $spacing-2xl;
    text-decoration: none;
  }
}
</style>
